import { useMemo } from 'react';
import { ExtrudeGeometry } from 'three';
import { floorPlankProfile } from './crossSections';
import { SCALE_IN_TO_UNIT, type TreadPlacement } from '../types';
import { MATERIALS } from '../materials';
import { diamondMeshAlphaMap, ribPatternNormalMap } from '../materials/proceduralTextures';
import { useSceneSelection } from '../selection/useSceneSelection';

interface Props {
  placement: TreadPlacement;
  floor: 'plein' | 'mailles';
  stockLengthIn?: number;
  partNumber?: string;
}

export function Tread({ placement, floor, stockLengthIn, partNumber }: Props) {
  const lengthIn = placement.lengthIn;
  const widthIn = placement.widthIn;

  const geometry = useMemo(() => {
    const profile = floorPlankProfile();
    const g = new ExtrudeGeometry(profile, { depth: lengthIn, bevelEnabled: false });
    g.translate(0, 0, -lengthIn / 2);
    g.scale(SCALE_IN_TO_UNIT, SCALE_IN_TO_UNIT, SCALE_IN_TO_UNIT);
    // ADJ-03: shift geometry DOWN by plank height (2") so that the TOP of the
    // walking surface sits exactly at position.y (= the nominal tread elevation).
    // Without this, the BOTTOM of the profile was at position.y and the top was
    // at position.y + 2", placing the walking surface 2" above the expected level.
    g.translate(0, -2 * SCALE_IN_TO_UNIT, 0);
    return g;
  }, [lengthIn]);

  const ribbedMaterial = useMemo(() => {
    const tex = ribPatternNormalMap();
    tex.repeat.set(lengthIn / 6, 1);
    tex.needsUpdate = true;
    const m = MATERIALS.aluminumBrushed.clone();
    m.normalMap = tex;
    m.normalScale.set(0.4, 0.4);
    return m;
  }, [lengthIn]);

  const maillesMaterial = useMemo(() => {
    const tex = diamondMeshAlphaMap();
    tex.repeat.set(lengthIn / 6, 1);
    tex.needsUpdate = true;
    const m = MATERIALS.aluminumPerforated.clone();
    m.alphaMap = tex;
    m.transparent = true;
    m.alphaTest = 0.5;
    return m;
  }, [lengthIn]);

  const material = floor === 'plein' ? ribbedMaterial : maillesMaterial;

  const plankCount = Math.max(1, Math.round(widthIn / 6));
  const planksOffsetStart = -((plankCount - 1) * 6) / 2;
  const inferredPartNumber = partNumber ?? 'A-31713';
  const isCut = stockLengthIn !== undefined && stockLengthIn !== lengthIn;

  return (
    <group
      position={[placement.position.x, placement.position.y, placement.position.z]}
      rotation={[placement.pitchAngle, placement.rotationY, 0]}
    >
      {Array.from({ length: plankCount }).map((_, i) => {
        const xOff = (planksOffsetStart + i * 6) * SCALE_IN_TO_UNIT;
        return (
          <mesh
            key={i}
            geometry={geometry}
            material={material}
            position={[xOff, 0, 0]}
            castShadow
            receiveShadow
            userData={{
              partNumber: inferredPartNumber,
              kind: 'tread',
              lengthIn,
              widthIn,
              cutOnSite: isCut,
              stockLengthIn,
              refId: `${placement.id}-plank-${i}`,
            }}
            onPointerOver={(e) => {
              e.stopPropagation();
              useSceneSelection.getState().setHovered(`${placement.id}-plank-${i}`);
            }}
            onPointerOut={(e) => {
              e.stopPropagation();
              useSceneSelection.getState().setHovered(null);
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              useSceneSelection.getState().setSelected(`${placement.id}-plank-${i}`);
            }}
          />
        );
      })}
    </group>
  );
}
